import { api } from './api.js';
import { esc, fmt$, CATEGORY_COLORS, showToast, presentSheet, closeSheet} from './utils.js';

// ── SETTINGS PAGE ─────────────────────────────────────────────────────────

export async function renderSettings(el) {
  el.innerHTML = loadingSkeleton();

  let [settingsData, cats, expenseCats, rateHistory, allocs, classes, expenses] = await Promise.all([
    api.settings.get().catch(() => null),
    api.categories().catch(() => []),
    api.expenseCategories().catch(() => []),
    api.settings.rateHistory().catch(() => []),
    api.settings.getTimeAlloc().catch(() => []),
    api.blocks.classes.list().catch(() => []),
    api.expenses.list().catch(() => []),
  ]);

  if (!settingsData) {
    el.innerHTML = `<p style="color:var(--negative)">Failed to load settings.</p>`;
    return;
  }

  // name/email/birthday now come from GET /api/settings itself (settings.ts
  // folds them in from `users`) instead of a separate api.auth.session()
  // call — see that route's comment for why.
  const user = settingsData;

  // Build allocation records mapping categoryId -> {weeklyHours, minWeeklyHours, maxWeeklyHours}
  const allocMap = {};
  for (const a of allocs) {
    allocMap[a.categoryId] = a;
  }

  el.innerHTML = `
    <div style="max-width:640px;margin:0 auto">

      <h1 style="font-size:1.4rem;font-weight:800;margin-bottom:var(--space-6)">Settings</h1>

      <!-- One consolidated save for Profile (name/birthday)/Income/Goals/Sleep
           Window/Time Allocation — password and email changes stay separate,
           dedicated flows above. Hidden until something in those sections
           actually changes. -->
      <div class="settings-save-bar" id="settings-save-bar" style="display:none">
        <span style="font-size:.85rem;color:var(--text-secondary)">You have unsaved changes</span>
        <div style="display:flex;gap:var(--space-2)">
          <button class="btn btn-ghost" id="settings-discard-btn" style="padding:8px 16px">Discard</button>
          <button class="btn btn-primary" id="settings-save-btn" style="padding:8px 16px">Save changes</button>
        </div>
      </div>

      <!-- ── PROFILE ──────────────────────────────────────────────────────── -->
      <div class="settings-section">
        <div class="settings-section-title">Profile</div>
        <div class="card" style="margin-bottom:0">
          <div class="form-group">
            <label class="form-label">Name</label>
            <input type="text" id="set-name" value="${esc(user.name ?? '')}" placeholder="Your name">
          </div>
          <div class="form-group">
            <label class="form-label">Email</label>
            <input type="email" id="set-email" value="${esc(user.email ?? '')}" disabled
              style="opacity:.5;cursor:not-allowed">
          </div>

          <div id="change-email-form" style="display:none;margin-bottom:var(--space-4);padding:var(--space-3);background:var(--bg-tertiary);border-radius:var(--radius-md)">
            <div class="form-group" id="change-email-step-new">
              <label class="form-label">New email</label>
              <input type="email" id="new-email-input" placeholder="you@example.com" autocomplete="email">
            </div>
            <div class="form-group hidden" id="change-email-step-code">
              <label class="form-label">Verification code</label>
              <input type="text" id="change-email-otp" inputmode="numeric" pattern="[0-9]*" maxlength="6"
                placeholder="123456" autocomplete="one-time-code" style="letter-spacing:4px;font-family:var(--font-mono)">
              <div class="field-helper">We sent a 6-digit code to <strong id="change-email-target"></strong>. It expires in 5 minutes.</div>
            </div>
            <div id="change-email-err" class="auth-error"></div>
            <div style="display:flex;gap:var(--space-2)">
              <button class="btn btn-primary" id="change-email-action-btn" style="padding:8px 16px">Send code</button>
              <button class="btn btn-ghost" id="cancel-change-email" style="padding:8px 16px">Cancel</button>
            </div>
          </div>
          <button class="btn btn-ghost" id="show-change-email-btn" style="margin-bottom:var(--space-4);width:100%;justify-content:center">Change email</button>
          <div class="form-group">
            <label class="form-label">Birthday <span style="color:var(--text-tertiary);font-weight:400">(optional)</span></label>
            <div class="birthday-row">
              <input type="number" id="set-bday-m" placeholder="MM" min="1" max="12" value="${esc(user.birthMonth ?? '')}">
              <input type="number" id="set-bday-d" placeholder="DD" min="1" max="31" value="${esc(user.birthDay ?? '')}">
              <input type="number" id="set-bday-y" placeholder="YYYY" min="1900" max="2020" value="${esc(user.birthYear ?? '')}">
            </div>
            <div class="field-helper">Used for annual net worth snapshot reminder 🎂</div>
          </div>
          <div id="profile-err" class="auth-error"></div>
        </div>

        <div id="change-password-form" style="display:none;margin-top:var(--space-3);padding:var(--space-3);background:var(--bg-tertiary);border-radius:var(--radius-md)">
          <div class="form-group">
            <label class="form-label">Current password</label>
            <input type="password" id="pw-current" autocomplete="current-password">
          </div>
          <div class="form-group">
            <label class="form-label">New password</label>
            <input type="password" id="pw-new" autocomplete="new-password" minlength="12">
            <div class="field-helper" style="margin-top:-8px">At least 12 characters.</div>
          </div>
          <div class="form-group">
            <label class="form-label">Confirm new password</label>
            <input type="password" id="pw-confirm" autocomplete="new-password">
          </div>
          <div class="form-group">
            <label class="form-label">Verification code</label>
            <input type="text" id="pw-otp" inputmode="numeric" pattern="[0-9]*" maxlength="6"
              placeholder="123456" autocomplete="one-time-code" style="letter-spacing:4px;font-family:var(--font-mono)">
            <div class="field-helper">
              <a href="#" id="pw-send-otp" style="color:inherit;text-decoration:underline">Send a code to your email</a>
              to confirm this change.
            </div>
          </div>
          <div class="form-group">
            <label style="display:flex;align-items:center;gap:var(--space-2);cursor:pointer;font-size:.875rem">
              <input type="checkbox" id="pw-revoke-others"> Sign out all other devices
            </label>
            <div class="field-helper" style="margin-top:4px">Ends any other active session on this account (other browsers, other devices). This one stays signed in.</div>
          </div>
          <div id="password-err" class="auth-error"></div>
          <div style="display:flex;gap:var(--space-2)">
            <button class="btn btn-primary" id="save-password" style="padding:8px 16px">Change password</button>
            <button class="btn btn-ghost" id="cancel-password" style="padding:8px 16px">Cancel</button>
          </div>
        </div>
        <button class="btn btn-ghost" id="show-change-password-btn" style="margin-top:var(--space-3);width:100%;justify-content:center">Change password</button>
      </div>

      <!-- ── INCOME ───────────────────────────────────────────────────────── -->
      <div class="settings-section">
        <div class="settings-section-title">Income</div>
        <div class="card" style="margin-bottom:0">
          <div class="form-group">
            <label class="form-label">Income type</label>
            <select id="set-income-type">
              ${['hourly_commission','hourly','commission','salary'].map(v => `
                <option value="${v}" ${settingsData.incomeType === v ? 'selected' : ''}>
                  ${{ hourly_commission: 'Hourly + Commission', hourly: 'Hourly only', commission: 'Commission only', salary: 'Salary' }[v]}
                </option>`).join('')}
            </select>
          </div>
          <div id="hourly-section" style="display:${settingsData.incomeType === 'salary' || settingsData.incomeType === 'commission' ? 'none' : 'block'}">
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Gross hourly ($)</label>
                <input type="number" id="set-gross" value="${settingsData.grossHourlyRate ?? ''}" step="0.01" placeholder="0.00" style="font-family:var(--font-mono)">
              </div>
              <div class="form-group">
                <label class="form-label">Net take-home ($)</label>
                <input type="number" id="set-net" value="${settingsData.netHourlyRate ?? ''}" step="0.01" placeholder="0.00" style="font-family:var(--font-mono)">
              </div>
            </div>
            <div class="field-helper" style="margin-top:-8px;margin-bottom:var(--space-4)">OT rates auto-calculated at 1.5×</div>
          </div>
          <div id="salary-section" style="display:${settingsData.incomeType === 'salary' ? 'block' : 'none'}">
            <div class="form-group">
              <label class="form-label">Net per paycheck ($)</label>
              <input type="number" id="set-salary" value="${settingsData.salaryPerPaycheck ?? ''}" step="0.01" placeholder="0.00" style="font-family:var(--font-mono)">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Pay period start <span style="color:var(--text-tertiary);font-weight:400">(anchor date)</span></label>
              <input type="date" id="set-anchor" value="${settingsData.payPeriodAnchor ?? ''}">
              <div class="field-helper">The date your current pay period began. Every payday, commission date and pay-period figure in the app counts forward from here.</div>
            </div>
            <div class="form-group">
              <label class="form-label">Pay period length</label>
              <select id="set-period-len">
                <option value="7"  ${settingsData.payPeriodLengthDays === 7  ? 'selected' : ''}>Weekly (7 days)</option>
                <option value="14" ${settingsData.payPeriodLengthDays === 14 ? 'selected' : ''}>Bi-weekly (14 days)</option>
                <option value="30" ${settingsData.payPeriodLengthDays === 30 ? 'selected' : ''}>Monthly (30 days)</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Payday</label>
              <select id="set-payday-weekday">
                ${['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map((day, i) => `
                  <option value="${i}" ${(settingsData.paydayWeekday ?? 5) === i ? 'selected' : ''}>${day}</option>`).join('')}
              </select>
            </div>
            <div class="form-group" id="commission-payday-group" style="display:${settingsData.incomeType === 'commission' || settingsData.incomeType === 'hourly_commission' ? 'block' : 'none'}">
              <label class="form-label">Commission lands on</label>
              <select id="set-commission-index">
                ${['1st','2nd','3rd','4th','5th'].map((label, i) => `
                  <option value="${i + 1}" ${(settingsData.commissionPaydayIndex ?? 2) === i + 1 ? 'selected' : ''}>${label} payday of the month</option>`).join('')}
              </select>
            </div>
          </div>
          <div class="field-helper" style="margin-top:-8px;margin-bottom:var(--space-4)">Which day of the week you're actually paid, and (for commission income) which payday of the month carries it — defaults match the app's original built-in schedule (Friday, 2nd payday).</div>
          <div class="form-group">
            <label class="form-label">Work hours by season</label>
            <div class="field-helper" style="margin-top:-2px;margin-bottom:var(--space-2)">Months you work full-time — tap to toggle. The rest are treated as part-time (e.g. during school).</div>
            <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:var(--space-2)" id="fulltime-months-grid">
              ${renderMonthToggles(settingsData.fullTimeMonths)}
            </div>
            <div class="form-row" style="margin-top:var(--space-3)">
              <div class="form-group">
                <label class="form-label">Full-time hrs/week</label>
                <input type="number" id="set-fulltime-hours" value="${settingsData.fullTimeWeeklyHours ?? 40}" min="0" max="168" step="1" style="font-family:var(--font-mono)">
              </div>
              <div class="form-group">
                <label class="form-label">Part-time hrs/week</label>
                <input type="number" id="set-parttime-hours" value="${settingsData.partTimeWeeklyHours ?? 20}" min="0" max="168" step="1" style="font-family:var(--font-mono)">
              </div>
            </div>
            <div class="field-helper" style="margin-top:-8px;margin-bottom:var(--space-4)">Used to estimate income for months you haven't logged yet — projections use whichever applies to that month above.</div>
          </div>
          <div id="income-err" class="auth-error"></div>
        </div>

        <div style="margin-top:var(--space-3)">
          <div style="font-size:.75rem;color:var(--text-tertiary);font-weight:600;text-transform:uppercase;letter-spacing:.06em;margin-bottom:var(--space-2)">Rate history</div>
          ${rateHistory.length > 0 ? `
          <div id="rate-history-list">
            ${rateHistory.slice(0, 5).map(r => `
              <div class="cat-row" data-rate-id="${esc(r.id)}" style="justify-content:space-between;padding:var(--space-2) 0;border-bottom:1px solid var(--bg-tertiary);font-size:.8rem">
                <span style="color:var(--text-secondary)">${esc(r.effectiveDate)}</span>
                <span class="font-mono">${fmt$(r.grossRate)}/hr gross · ${fmt$(r.netRate)}/hr net</span>
                <button class="btn btn-ghost delete-rate-btn" data-rate-id="${esc(r.id)}" style="padding:2px 6px;font-size:.7rem">✕</button>
              </div>`).join('')}
          </div>` : `<p class="color-tertiary text-sm" style="padding:var(--space-2) 0">No rate changes logged yet.</p>`}

          <div id="add-rate-form" style="display:none;margin-top:var(--space-3);padding:var(--space-3);background:var(--bg-tertiary);border-radius:var(--radius-md)">
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Gross ($/hr)</label>
                <input type="number" id="new-rate-gross" step="0.01" style="font-family:var(--font-mono)">
              </div>
              <div class="form-group">
                <label class="form-label">Net ($/hr)</label>
                <input type="number" id="new-rate-net" step="0.01" style="font-family:var(--font-mono)">
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">Effective date</label>
              <input type="date" id="new-rate-date" value="${new Date().toISOString().split('T')[0]}">
            </div>
            <div class="field-helper" style="margin-top:-8px;margin-bottom:var(--space-3)">Can be in the past or future — logging this does not change your current live rate above.</div>
            <div id="rate-add-err" class="auth-error"></div>
            <div style="display:flex;gap:var(--space-2)">
              <button class="btn btn-primary" id="save-rate-entry" style="padding:8px 16px">Log rate change</button>
              <button class="btn btn-ghost" id="cancel-rate-entry" style="padding:8px 16px">Cancel</button>
            </div>
          </div>
          <button class="btn btn-ghost" id="show-add-rate-btn" style="margin-top:var(--space-3);width:100%;justify-content:center">+ Add rate change</button>
        </div>
      </div>

      <!-- ── FINANCIAL GOALS ───────────────────────────────────────────────── -->
      <div class="settings-section">
        <div class="settings-section-title">Financial Goals</div>
        <div class="card" style="margin-bottom:0">
          <div class="form-group">
            <label class="form-label">Net worth goal ($)</label>
            <input type="number" id="set-goal" value="${settingsData.goalAmount ?? 1000000}" step="10000" style="font-family:var(--font-mono)">
          </div>
          <div class="form-group">
            <label class="form-label">Expected annual return</label>
            <div style="display:flex;align-items:center;gap:var(--space-3)">
              <input type="range" id="set-return" min="3" max="15" step="0.5" value="${settingsData.returnRatePct ?? 7}" style="flex:1;accent-color:var(--accent)">
              <span class="font-mono" style="font-weight:700;width:40px;text-align:right" id="return-disp">${settingsData.returnRatePct ?? 7}%</span>
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">Monthly investment ($)</label>
            <input type="number" id="set-invest" value="${settingsData.monthlyInvestAmt ?? 300}" step="50" style="font-family:var(--font-mono)">
          </div>
          <div id="goals-err" class="auth-error"></div>
        </div>
      </div>

      <!-- ── SLEEP WINDOW ─────────────────────────────────────────────────── -->
      <div class="settings-section">
        <div class="settings-section-title">Sleep Window</div>
        <div class="card" style="margin-bottom:0">
          <div class="field-helper" style="margin-top:-2px;margin-bottom:var(--space-3)">
            When you sleep, not just how long — this lets the Schedule Assistant suggest times that actually fit your day (including overnight shifts or late classes), and sets your default weekly task capacity below.
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Sleep starts around</label>
              <input type="time" id="set-sleep-start" value="${settingsData.sleepStartTime ?? '00:00'}">
            </div>
            <div class="form-group">
              <label class="form-label">Hours of sleep</label>
              <input type="number" id="set-sleep-hours" value="${settingsData.sleepDurationHours ?? 6}" min="0" max="24" step="0.5" style="font-family:var(--font-mono)">
            </div>
          </div>
          <div class="field-helper" id="capacity-cap-display" style="margin-top:-8px;margin-bottom:var(--space-3)">
            Weekly task capacity: ${(168 - 7 * (settingsData.sleepDurationHours ?? 6)).toFixed(1)}h (168 minus 7× sleep hours)
          </div>
          <div id="sleep-err" class="auth-error"></div>
        </div>
      </div>

      <!-- ── TIME ALLOCATIONS ─────────────────────────────────────────────── -->
      <div class="settings-section">
        <div class="settings-section-title">Weekly Time Allocation Targets</div>
        <div class="card" style="margin-bottom:0">
          <div class="field-helper" style="margin-top:-2px;margin-bottom:var(--space-3)">
            Targets across all categories can't exceed your weekly task capacity (${(168 - 7 * (settingsData.sleepDurationHours ?? 6)).toFixed(1)}h, from the Sleep Window above).
          </div>
          ${cats.map(cat => {
            const a = allocMap[cat.id] ?? {};
            const target = a.weeklyHours ?? 0;
            const min    = a.minWeeklyHours ?? '';
            const max    = a.maxWeeklyHours ?? '';
            return `
              <div style="padding:var(--space-3) 0;border-bottom:1px solid var(--bg-tertiary)">
                <div style="display:flex;align-items:center;gap:var(--space-2);margin-bottom:var(--space-2)">
                  <div style="width:10px;height:10px;border-radius:50%;background:${esc(cat.color)}"></div>
                  <span style="font-size:.875rem;font-weight:500">${esc(cat.name)}</span>
                </div>
                <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:var(--space-2)">
                  <div>
                    <label style="font-size:.7rem;color:var(--text-tertiary)">Min</label>
                    <input type="number" class="alloc-min-input" data-cat-id="${esc(cat.id)}" value="${min}" min="0" max="168" step="0.5" placeholder="—"
                      style="width:100%;text-align:right;font-family:var(--font-mono);padding:6px">
                  </div>
                  <div>
                    <label style="font-size:.7rem;color:var(--text-tertiary)">Target</label>
                    <input type="number" class="alloc-hours-input" data-cat-id="${esc(cat.id)}" value="${target}" min="0" max="168" step="0.5"
                      style="width:100%;text-align:right;font-family:var(--font-mono);padding:6px">
                  </div>
                  <div>
                    <label style="font-size:.7rem;color:var(--text-tertiary)">Max</label>
                    <input type="number" class="alloc-max-input" data-cat-id="${esc(cat.id)}" value="${max}" min="0" max="168" step="0.5" placeholder="—"
                      style="width:100%;text-align:right;font-family:var(--font-mono);padding:6px">
                  </div>
                </div>
              </div>`;
          }).join('')}
          <div id="allocs-err" class="auth-error" style="margin-top:var(--space-3)"></div>
        </div>
      </div>

      <!-- ── CATEGORIES ───────────────────────────────────────────────────── -->
      <div class="settings-section">
        <div class="settings-section-title">Time categories</div>
        <div class="card" style="margin-bottom:0" id="cat-list">
          <!-- Renamed from "Category Management" (#63): with two taxonomies in the
               product, a section called just "categories" no longer says which. -->
          <div class="field-helper" style="margin-bottom:var(--space-3)">Used by tasks, calendar blocks and your weekly time targets. Money uses its own set below.</div>
          ${renderCategoryList(cats)}
        </div>
        <button class="btn btn-ghost" id="add-cat-btn" style="margin-top:var(--space-3);font-size:.875rem">+ Add category</button>
      </div>

      <!-- ── EXPENSE CATEGORIES (#63 / UX_AUDIT B-4) ───────────────────────── -->
      <div class="settings-section">
        <div class="settings-section-title">Expense categories</div>
        <div class="card" style="margin-bottom:0" id="expense-cat-list">
          <div class="field-helper" style="margin-bottom:var(--space-3)">Used only for spending. Keeping these separate from time categories is what lets the Budget view answer "what do I spend on housing?" — rename or recolour the defaults freely.</div>
          ${renderExpenseCategoryList(expenseCats)}
        </div>
        <button class="btn btn-ghost" id="add-expense-cat-btn" style="margin-top:var(--space-3);font-size:.875rem">+ Add expense category</button>
      </div>

      <!-- ── APPEARANCE ───────────────────────────────────────────────────── -->
      <div class="settings-section">
        <div class="settings-section-title">Appearance</div>
        <div class="card" style="margin-bottom:0">
          <div class="form-group" style="margin-bottom:0">
            <label class="form-label">Theme</label>
            <div style="display:flex;gap:var(--space-3);margin-top:var(--space-2)">
              ${['dark','light'].map(t => {
                // No light theme exists in the CSS yet (UX_AUDIT F-4). This control used to
                // accept the choice, persist it, and toast "Theme: light" while the app stayed
                // dark - a control that confirms a change it cannot make. Light is disabled and
                // labelled until a real palette ships, and Dark always renders as the active one
                // regardless of a previously-saved `light` value, because dark is what is on screen.
                const disabled = t === 'light';
                const active   = t === 'dark';
                return `
                <button class="theme-btn${active ? ' active' : ''}" data-theme="${t}"${disabled ? ' disabled aria-disabled="true" title="A light theme hasn\'t been built yet."' : ''}
                  style="flex:1;padding:var(--space-3);border-radius:var(--radius-md);border:2px solid ${active ? 'var(--accent)' : 'var(--bg-tertiary)'};background:${active ? 'rgba(var(--accent-rgb),.08)' : 'var(--bg-tertiary)'};font-weight:600;transition:all .15s;${disabled ? 'opacity:.5;cursor:not-allowed' : ''}">
                  ${t === 'dark' ? '🌙 Dark' : '☀️ Light'}
                  ${disabled ? `<span style="display:block;font-size:.65rem;font-weight:500;color:var(--text-tertiary);margin-top:2px">Coming soon</span>` : ''}
                </button>`;
              }).join('')}
            </div>
            <div class="field-helper">The app is dark-only for now. A light theme is on the roadmap.</div>
          </div>
        </div>
      </div>

      <!-- ── DATA ────────────────────────────────────────────────────────── -->
      <!-- ── RECURRING EXPENSES ───────────────────────────────────────────── -->
      <div class="settings-section">
        <div class="settings-section-title">Recurring Expenses</div>
        <div class="card" style="margin-bottom:0">
          <div class="field-helper" style="margin-bottom:var(--space-4)">These count toward every month's budget automatically until their end date (or forever, if none is set).</div>
          <div id="recurring-exp-list">${renderRecurringExpenses(expenses, cats)}</div>
          <button class="btn btn-ghost" id="add-recurring-exp-btn" style="margin-top:var(--space-3);width:100%;justify-content:center">+ Add recurring expense</button>
        </div>
      </div>

      <!-- ── COURSE SCHEDULE ─────────────────────────────────────────────── -->
      <div class="settings-section">
        <div class="settings-section-title">Course schedule</div>
        <div class="card" style="margin-bottom:0">
          <div class="field-helper" style="margin-bottom:var(--space-4)">Recurring class slots appear automatically on your calendar each week they're in session.</div>
          <div id="class-list">${renderCourseSchedule(classes)}</div>
          <button class="btn btn-ghost" id="add-class-btn" style="margin-top:var(--space-3);width:100%;justify-content:center">+ Add course</button>
        </div>
      </div>

      <!-- ── DATA ──────────────────────────────────────────────────────────── -->
      <div class="settings-section">
        <div class="settings-section-title">Data</div>
        <div class="card" style="margin-bottom:0;display:flex;flex-direction:column;gap:var(--space-3)">
          <button class="btn btn-ghost" id="export-btn" style="justify-content:flex-start">📤 Export data as JSON</button>
          <input type="file" id="import-file-input" accept="application/json" style="display:none">
          <button class="btn btn-ghost" id="import-btn" style="justify-content:flex-start">📥 Import from backup</button>
          ${!settingsData.hasSampleData ? `<button class="btn btn-ghost" id="load-sample-btn" style="justify-content:flex-start">✨ Load sample data</button>` : ''}
          ${settingsData.hasSampleData ? `<button class="btn btn-ghost" id="clear-sample-btn" style="justify-content:flex-start;color:var(--caution)">🗑 Clear sample data</button>` : ''}
          <button class="btn btn-ghost" id="signout-btn" style="justify-content:flex-start">← Sign out</button>
          <button class="btn btn-danger" id="delete-account-btn" style="justify-content:flex-start">⚠ Delete account</button>
        </div>
      </div>

      <div style="height:40px"></div>
    </div>`;

  buildSettingsNav(el);
  wireSettings(el, settingsData, user, cats, expenseCats, classes, expenses);
}

// UX_AUDIT P-2: Settings is 5,094px against a 900px viewport — 29 labelled fields
// across 10 sections with no sidebar, no anchors and no search, so finding one
// setting means scrolling blind. This adds a sticky jump-nav.
//
// Built by reading the rendered DOM rather than from a hardcoded list of section
// names: the sections already exist and already carry their own titles, so
// deriving the nav from them means adding, renaming or reordering a section
// updates the nav automatically. A parallel hardcoded list is exactly the kind of
// second source of truth that silently goes stale.
function buildSettingsNav(el) {
  const sections = [...el.querySelectorAll('.settings-section')];
  const entries  = sections.map((sec, i) => {
    const title = sec.querySelector('.settings-section-title')?.textContent?.trim();
    if (!title) return null;
    const id = `settings-sec-${i}`;
    sec.id = id;
    return { id, title };
  }).filter(Boolean);
  if (entries.length < 2) return;

  const nav = document.createElement('div');
  nav.className = 'settings-nav';
  nav.innerHTML = entries
    .map(e => `<button type="button" class="settings-nav-chip" data-target="${e.id}">${esc(e.title)}</button>`)
    .join('');

  const heading = el.querySelector('h1');
  heading?.insertAdjacentElement('afterend', nav);

  nav.addEventListener('click', (ev) => {
    const chip = ev.target.closest('.settings-nav-chip');
    if (!chip) return;
    const target = el.querySelector(`#${chip.dataset.target}`);
    if (!target) return;
    // `scroll-margin-top` on .settings-section keeps the sticky nav from covering
    // the heading it just scrolled to.
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

// ── RENDER MONTH TOGGLES (Work hours by season) ──────────────────────────

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function renderMonthToggles(fullTimeMonthsStr) {
  const active = new Set((fullTimeMonthsStr ?? '').split(',').map(s => parseInt(s, 10)).filter(Boolean));
  return MONTH_LABELS.map((label, i) => {
    const month = i + 1;
    const isActive = active.has(month);
    return `
      <button type="button" class="month-toggle-btn${isActive ? ' active' : ''}" data-month="${month}"
        style="padding:8px 0;border-radius:var(--radius-md);border:2px solid ${isActive ? 'var(--accent)' : 'var(--bg-tertiary)'};background:${isActive ? 'rgba(var(--accent-rgb),.12)' : 'var(--bg-tertiary)'};font-size:.8rem;font-weight:600;color:${isActive ? 'var(--accent)' : 'var(--text-secondary)'}">
        ${label}
      </button>`;
  }).join('');
}

// ── RENDER RECURRING EXPENSES ────────────────────────────────────────────

function renderRecurringExpenses(expenses, cats) {
  const recurring = expenses.filter(e => e.isRecurring);
  if (!recurring.length) return `<p class="color-tertiary text-sm" style="text-align:center;padding:20px 0">No recurring expenses yet.</p>`;
  const catById = cats.reduce((acc, c) => { acc[c.id] = c; return acc; }, {});
  const today = new Date().toISOString().split('T')[0];

  const freqLabel = (days) => {
    if (days === 7)   return '/wk';
    if (days === 91)  return '/qtr';
    if (days === 365) return '/yr';
    return '/mo'; // null (legacy/"Monthly") or any other value defaults to monthly framing
  };

  return recurring.map(e => {
    const cat = catById[e.categoryId];
    const ended = e.recurringEndDate && e.recurringEndDate < today;
    return `
      <div class="cat-row" data-exp-id="${esc(e.id)}" style="align-items:flex-start;gap:var(--space-3);padding:var(--space-3) 0;border-bottom:1px solid var(--bg-tertiary)">
        <div style="flex:1">
          <div style="font-weight:600;font-size:.9rem;${ended ? 'color:var(--text-tertiary)' : ''}">${esc(e.name)}
            ${ended ? '<span style="font-size:.7rem;color:var(--text-tertiary);font-weight:400"> · ended</span>' : ''}</div>
          <div style="font-size:.8rem;color:var(--text-secondary);margin-top:2px;display:flex;align-items:center;gap:6px">
            <span class="font-mono">${fmt$(e.amount)}${freqLabel(e.recurringIntervalDays)}</span>
            ${cat ? `<span style="width:6px;height:6px;border-radius:50%;background:${esc(cat.color)};display:inline-block"></span><span>${esc(cat.name)}</span>` : ''}
          </div>
          <div style="font-size:.7rem;color:var(--text-tertiary)">${e.recurringEndDate ? `Ends ${esc(e.recurringEndDate)}` : 'No end date'}</div>
        </div>
        <button class="btn btn-ghost edit-recurring-exp-btn" data-exp-id="${esc(e.id)}" style="padding:4px 8px;font-size:.75rem;flex-shrink:0">Edit</button>
        <button class="btn btn-danger delete-recurring-exp-btn" data-exp-id="${esc(e.id)}" style="padding:4px 8px;font-size:.75rem;flex-shrink:0">✕</button>
      </div>`;
  }).join('');
}

// ── RENDER COURSE SCHEDULE ───────────────────────────────────────────────────

function renderCourseSchedule(classes) {
  if (!classes.length) return `<p class="color-tertiary text-sm" style="text-align:center;padding:20px 0">No courses yet. Add your first class above.</p>`;
  const DAYS = { mon:'Mo', tue:'Tu', wed:'We', thu:'Th', fri:'Fr', sat:'Sa', sun:'Su' };
  return classes.map(cls => `
    <div class="cat-row" style="align-items:flex-start;gap:var(--space-3);padding:var(--space-3) 0;border-bottom:1px solid var(--bg-tertiary)">
      <div style="flex:1">
        <div style="font-weight:600;font-size:.9rem">${esc(cls.courseCode)} — ${esc(cls.courseName)}</div>
        <div style="font-size:.75rem;color:var(--text-secondary);margin-top:2px">
          ${cls.dayOfWeek.split(',').map(d => DAYS[d.trim()] ?? d.trim()).join(' ')}
          · ${cls.startTime}–${cls.endTime}
          ${cls.location ? `· ${esc(cls.location)}` : cls.isOnline ? '· Online' : ''}
        </div>
        <div style="font-size:.7rem;color:var(--text-tertiary)">${cls.semesterStart} → ${cls.semesterEnd}</div>
      </div>
      <button class="btn btn-ghost edit-class-btn" data-class-id="${esc(cls.id)}" style="padding:4px 8px;font-size:.75rem;flex-shrink:0">Edit</button>
      <button class="btn btn-danger delete-class-btn" data-class-id="${esc(cls.id)}" style="padding:4px 8px;font-size:.75rem;flex-shrink:0">✕</button>
    </div>`).join('');
}

// ── RENDER CATEGORY LIST ─────────────────────────────────────────────────────

function renderCategoryList(cats) {
  if (!cats.length) return `<p class="color-tertiary text-sm" style="text-align:center;padding:20px 0">No categories yet.</p>`;
  return cats.map(cat => `
    <div class="cat-row" data-cat-id="${esc(cat.id)}">
      <div class="cat-dot-preview" style="background:${esc(cat.color)};width:12px;height:12px;border-radius:50%;flex-shrink:0"></div>
      <span style="flex:1;font-size:.9rem">${esc(cat.name)}</span>
      ${cat.isSystem ? `<span style="font-size:.7rem;color:var(--text-tertiary)">system</span>` : `
        <button class="btn btn-ghost edit-cat-btn" data-cat-id="${esc(cat.id)}" style="padding:4px 8px;font-size:.75rem">Edit</button>
        <button class="btn btn-danger delete-cat-btn" data-cat-id="${esc(cat.id)}" style="padding:4px 8px;font-size:.75rem">✕</button>
      `}
    </div>`).join('');
}

// Deliberately NOT identical to renderCategoryList above: a default expense
// category can be renamed and recoloured (the nine defaults are a starting point,
// not a fixed vocabulary — the PATCH route allows it on purpose), while only
// deletion is restricted, since removing one would orphan the migration's backfill.
function renderExpenseCategoryList(cats) {
  if (!cats.length) return `<p class="color-tertiary text-sm" style="text-align:center;padding:20px 0">No expense categories yet.</p>`;
  return cats.map(cat => `
    <div class="cat-row" data-expense-cat-id="${esc(cat.id)}">
      <div class="cat-dot-preview" style="background:${esc(cat.color)};width:12px;height:12px;border-radius:50%;flex-shrink:0"></div>
      <span style="flex:1;font-size:.9rem">${esc(cat.name)}</span>
      ${cat.isSystem ? `<span style="font-size:.7rem;color:var(--text-tertiary)">default</span>` : ''}
      <button class="btn btn-ghost edit-expense-cat-btn" data-expense-cat-id="${esc(cat.id)}" style="padding:4px 8px;font-size:.75rem">Edit</button>
      ${cat.isSystem ? '' : `<button class="btn btn-danger delete-expense-cat-btn" data-expense-cat-id="${esc(cat.id)}" style="padding:4px 8px;font-size:.75rem">✕</button>`}
    </div>`).join('');
}

// ── WIRE ALL INTERACTIONS ────────────────────────────────────────────────────

function wireSettings(el, settingsData, user, cats, expenseCats = [], classes = [], expenses = []) {

  // Income type toggle
  el.querySelector('#set-income-type').addEventListener('change', (e) => {
    const t = e.target.value;
    el.querySelector('#hourly-section').style.display = (t === 'hourly' || t === 'hourly_commission') ? 'block' : 'none';
    el.querySelector('#salary-section').style.display = t === 'salary' ? 'block' : 'none';
    el.querySelector('#commission-payday-group').style.display = (t === 'commission' || t === 'hourly_commission') ? 'block' : 'none';
  });

  // Return rate slider
  el.querySelector('#set-return').addEventListener('input', (e) => {
    el.querySelector('#return-disp').textContent = `${e.target.value}%`;
  });

  // Master save — Profile (name/birthday)/Income/Goals/Sleep/Allocations
  // are consolidated into one "Save changes" action (see settings-save-bar
  // above); each section keeps its own request function + error slot so a
  // partial failure still tells you exactly which section didn't save.
  async function doProfileSave() {
    const name       = el.querySelector('#set-name').value.trim();
    const birthMonth = parseInt(el.querySelector('#set-bday-m').value) || undefined;
    const birthDay   = parseInt(el.querySelector('#set-bday-d').value) || undefined;
    const birthYear  = parseInt(el.querySelector('#set-bday-y').value) || undefined;
    await api.settings.updateProfile({ name, birthMonth, birthDay, birthYear });
  }

  // Change email — two steps (send code to the NEW address, then verify it)
  // via Better Auth's email-otp plugin (src/lib/auth.ts). The current
  // address is never re-verified (changeEmail.verifyCurrentEmail: false).
  let changeEmailStep = 'new'; // 'new' | 'code'
  let pendingNewEmail = null;

  function resetChangeEmailForm() {
    changeEmailStep = 'new';
    pendingNewEmail = null;
    el.querySelector('#change-email-form').style.display = 'none';
    el.querySelector('#show-change-email-btn').style.display = 'flex';
    el.querySelector('#change-email-step-new').classList.remove('hidden');
    el.querySelector('#change-email-step-code').classList.add('hidden');
    el.querySelector('#new-email-input').value = '';
    el.querySelector('#change-email-otp').value = '';
    el.querySelector('#change-email-action-btn').textContent = 'Send code';
    el.querySelector('#change-email-err').classList.remove('show');
  }

  el.querySelector('#show-change-email-btn')?.addEventListener('click', () => {
    el.querySelector('#change-email-form').style.display = 'block';
    el.querySelector('#show-change-email-btn').style.display = 'none';
  });
  el.querySelector('#cancel-change-email')?.addEventListener('click', resetChangeEmailForm);
  el.querySelector('#change-email-action-btn')?.addEventListener('click', async () => {
    const errEl = el.querySelector('#change-email-err');
    errEl.classList.remove('show');

    if (changeEmailStep === 'new') {
      const newEmail = el.querySelector('#new-email-input').value.trim();
      if (!newEmail) {
        errEl.textContent = 'Enter a new email address.';
        errEl.classList.add('show');
        return;
      }
      if (newEmail.toLowerCase() === (user.email ?? '').toLowerCase()) {
        errEl.textContent = 'That’s already your current email.';
        errEl.classList.add('show');
        return;
      }
      try {
        await api.auth.requestEmailChange(newEmail);
        pendingNewEmail = newEmail;
        changeEmailStep = 'code';
        el.querySelector('#change-email-step-code').classList.remove('hidden');
        el.querySelector('#change-email-target').textContent = newEmail;
        el.querySelector('#change-email-action-btn').textContent = 'Verify & change email';
        el.querySelector('#change-email-otp').focus();
      } catch (err) {
        errEl.textContent = err.message ?? 'Failed to send code';
        errEl.classList.add('show');
      }
      return;
    }

    // changeEmailStep === 'code'
    const otp = el.querySelector('#change-email-otp').value.trim();
    if (otp.length !== 6) {
      errEl.textContent = 'Enter the 6-digit code.';
      errEl.classList.add('show');
      return;
    }
    try {
      await api.auth.changeEmail(pendingNewEmail, otp);
      showToast('Email changed');
      resetChangeEmailForm();
      renderSettings(el);
    } catch (err) {
      errEl.textContent = err.message ?? 'Failed to change email';
      errEl.classList.add('show');
    }
  });

  // Change password
  el.querySelector('#show-change-password-btn')?.addEventListener('click', () => {
    el.querySelector('#change-password-form').style.display = 'block';
    el.querySelector('#show-change-password-btn').style.display = 'none';
  });
  function resetChangePasswordForm() {
    el.querySelector('#change-password-form').style.display = 'none';
    el.querySelector('#show-change-password-btn').style.display = 'flex';
    el.querySelector('#pw-current').value = '';
    el.querySelector('#pw-new').value = '';
    el.querySelector('#pw-confirm').value = '';
    el.querySelector('#pw-otp').value = '';
    el.querySelector('#pw-revoke-others').checked = false;
  }
  el.querySelector('#cancel-password')?.addEventListener('click', resetChangePasswordForm);

  el.querySelector('#pw-send-otp')?.addEventListener('click', async (e) => {
    e.preventDefault();
    const errEl = el.querySelector('#password-err');
    errEl.classList.remove('show');
    try {
      await api.settings.sendPasswordOtp();
      showToast('Code sent to your email');
    } catch (err) {
      errEl.textContent = err.message ?? 'Failed to send code';
      errEl.classList.add('show');
    }
  });

  el.querySelector('#save-password')?.addEventListener('click', async () => {
    const errEl = el.querySelector('#password-err');
    errEl.classList.remove('show');
    const currentPassword    = el.querySelector('#pw-current').value;
    const newPassword        = el.querySelector('#pw-new').value;
    const confirmPassword    = el.querySelector('#pw-confirm').value;
    const otp                 = el.querySelector('#pw-otp').value.trim();
    const revokeOtherSessions = el.querySelector('#pw-revoke-others').checked;
    if (!currentPassword || !newPassword) {
      errEl.textContent = 'Current and new password are both required.';
      errEl.classList.add('show');
      return;
    }
    if (newPassword.length < 12) {
      errEl.textContent = 'New password must be at least 12 characters.';
      errEl.classList.add('show');
      return;
    }
    if (newPassword !== confirmPassword) {
      errEl.textContent = 'New password and confirmation do not match.';
      errEl.classList.add('show');
      return;
    }
    if (otp.length !== 6) {
      errEl.textContent = 'Enter the 6-digit code sent to your email.';
      errEl.classList.add('show');
      return;
    }
    try {
      await api.auth.changePassword(currentPassword, newPassword, revokeOtherSessions, otp);
      showToast('Password changed');
      resetChangePasswordForm();
    } catch (err) {
      errEl.textContent = err.message ?? 'Failed to change password';
      errEl.classList.add('show');
    }
  });

  // Full-time month toggles (click to flip active state, saved with the rest
  // of the income form below rather than its own button/endpoint)
  el.querySelectorAll('.month-toggle-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const isActive = btn.classList.toggle('active');
      btn.style.borderColor  = isActive ? 'var(--accent)' : 'var(--bg-tertiary)';
      btn.style.background   = isActive ? 'rgba(var(--accent-rgb),.12)' : 'var(--bg-tertiary)';
      btn.style.color        = isActive ? 'var(--accent)' : 'var(--text-secondary)';
    });
  });

  async function doIncomeSave() {
    const incomeType          = el.querySelector('#set-income-type').value;
    const grossHourlyRate     = parseFloat(el.querySelector('#set-gross')?.value) || undefined;
    const netHourlyRate       = parseFloat(el.querySelector('#set-net')?.value)   || undefined;
    const salaryPerPaycheck   = parseFloat(el.querySelector('#set-salary')?.value) || undefined;
    const payPeriodAnchor     = el.querySelector('#set-anchor').value || undefined;
    const payPeriodLengthDays = parseInt(el.querySelector('#set-period-len').value) || undefined;
    const paydayWeekday       = parseInt(el.querySelector('#set-payday-weekday')?.value);
    const commissionPaydayIndex = (incomeType === 'commission' || incomeType === 'hourly_commission')
      ? parseInt(el.querySelector('#set-commission-index')?.value) || undefined
      : undefined; // don't send a stale/irrelevant index for non-commission income types
    const fullTimeMonths      = [...el.querySelectorAll('.month-toggle-btn.active')].map(b => parseInt(b.dataset.month, 10));
    const fullTimeWeeklyHours = parseFloat(el.querySelector('#set-fulltime-hours')?.value) || undefined;
    const partTimeWeeklyHours = parseFloat(el.querySelector('#set-parttime-hours')?.value) || undefined;
    await api.settings.update({ incomeType, grossHourlyRate, netHourlyRate, salaryPerPaycheck, payPeriodAnchor, payPeriodLengthDays, paydayWeekday, commissionPaydayIndex, fullTimeMonths, fullTimeWeeklyHours, partTimeWeeklyHours });
  }

  async function doSleepSave() {
    const sleepStartTime     = el.querySelector('#set-sleep-start').value || undefined;
    const sleepDurationHours = parseFloat(el.querySelector('#set-sleep-hours')?.value);
    await api.settings.update({ sleepStartTime, sleepDurationHours });
  }

  // Add rate change (manual, effective-dated entry)
  el.querySelector('#show-add-rate-btn')?.addEventListener('click', () => {
    el.querySelector('#add-rate-form').style.display = 'block';
    el.querySelector('#show-add-rate-btn').style.display = 'none';
  });
  el.querySelector('#cancel-rate-entry')?.addEventListener('click', () => {
    el.querySelector('#add-rate-form').style.display = 'none';
    el.querySelector('#show-add-rate-btn').style.display = 'flex';
  });
  el.querySelector('#save-rate-entry')?.addEventListener('click', async () => {
    const errEl = el.querySelector('#rate-add-err');
    errEl.classList.remove('show');
    const grossRate     = parseFloat(el.querySelector('#new-rate-gross').value);
    const netRate       = parseFloat(el.querySelector('#new-rate-net').value);
    const effectiveDate = el.querySelector('#new-rate-date').value;
    if (!grossRate || !netRate || !effectiveDate) {
      errEl.textContent = 'Gross rate, net rate, and effective date are all required.';
      errEl.classList.add('show');
      return;
    }
    try {
      await api.settings.addRateHistory({ grossRate, netRate, effectiveDate });
      showToast('Rate change logged');
      renderSettings(el);
    } catch (err) {
      errEl.textContent = err.message ?? 'Failed to save';
      errEl.classList.add('show');
    }
  });
  el.querySelectorAll('.delete-rate-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      if (!confirm('Delete this rate history entry?')) return;
      try {
        await api.settings.deleteRateHistory(btn.dataset.rateId);
        renderSettings(el);
      } catch (err) {
        showToast(err.message ?? 'Failed to delete', true);
      }
    });
  });

  // Save goals
  async function doGoalsSave() {
    const goalAmount       = parseFloat(el.querySelector('#set-goal').value) || 1_000_000;
    const returnRatePct    = parseFloat(el.querySelector('#set-return').value) || 7;
    const monthlyInvestAmt = parseFloat(el.querySelector('#set-invest').value) || 300;
    await api.settings.update({ goalAmount, returnRatePct, monthlyInvestAmt });
  }

  async function doAllocsSave() {
    const payload = [];
    el.querySelectorAll('.alloc-hours-input').forEach(input => {
      const catId  = input.dataset.catId;
      const minEl  = el.querySelector(`.alloc-min-input[data-cat-id="${catId}"]`);
      const maxEl  = el.querySelector(`.alloc-max-input[data-cat-id="${catId}"]`);
      payload.push({
        categoryId:     catId,
        weeklyHours:    parseFloat(input.value) || 0,
        minWeeklyHours: minEl.value === '' ? null : parseFloat(minEl.value),
        maxWeeklyHours: maxEl.value === '' ? null : parseFloat(maxEl.value),
      });
    });
    await api.settings.updateTimeAlloc(payload);
  }

  // Dirty-tracking + the master save/discard bar itself. Only these 5
  // sections' fields are watched — password, email-change, rate-history,
  // and category management all keep their own independent save actions
  // and are deliberately not part of this.
  let formDirty = false;
  const saveBar    = el.querySelector('#settings-save-bar');
  const saveBtn    = el.querySelector('#settings-save-btn');
  const discardBtn = el.querySelector('#settings-discard-btn');
  const sections = [
    { errId: 'profile-err', run: doProfileSave },
    { errId: 'income-err',  run: doIncomeSave },
    { errId: 'goals-err',   run: doGoalsSave },
    { errId: 'sleep-err',   run: doSleepSave },
    { errId: 'allocs-err',  run: doAllocsSave },
  ];
  const watchedFields = el.querySelectorAll(
    '#set-name, #set-bday-m, #set-bday-d, #set-bday-y, ' +
    '#set-income-type, #set-gross, #set-net, #set-salary, #set-anchor, #set-period-len, ' +
    '#set-payday-weekday, #set-commission-index, ' +
    '#set-fulltime-hours, #set-parttime-hours, .month-toggle-btn, ' +
    '#set-goal, #set-return, #set-invest, ' +
    '#set-sleep-start, #set-sleep-hours, ' +
    '.alloc-min-input, .alloc-hours-input, .alloc-max-input'
  );
  function markDirty() {
    if (formDirty) return;
    formDirty = true;
    saveBar.style.display = 'flex';
  }
  watchedFields.forEach(field => {
    field.addEventListener(field.tagName === 'BUTTON' ? 'click' : 'input', markDirty);
  });

  discardBtn.addEventListener('click', () => renderSettings(el));

  saveBtn.addEventListener('click', async () => {
    const originalLabel = saveBtn.textContent;
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving…';
    sections.forEach(s => el.querySelector(`#${s.errId}`)?.classList.remove('show'));

    const outcomes = await Promise.allSettled(sections.map(s => s.run()));
    let failCount = 0;
    outcomes.forEach((res, i) => {
      if (res.status === 'rejected') {
        failCount++;
        const errEl = el.querySelector(`#${sections[i].errId}`);
        if (errEl) {
          errEl.textContent = res.reason?.message ?? 'Failed to save';
          errEl.classList.add('show');
        }
      }
    });

    if (failCount === 0) {
      showToast('Settings saved');
      renderSettings(el); // re-render — e.g. sleep hours change the displayed weekly capacity elsewhere on this page
    } else {
      showToast(
        `${sections.length - failCount} of ${sections.length} sections saved — check errors above`,
        true,
      );
      saveBtn.disabled = false;
      saveBtn.textContent = originalLabel;
    }
  });

  // Theme buttons
  el.querySelectorAll('.theme-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      // Belt-and-braces: a disabled <button> fires no click, but this handler must never
      // persist or confirm a theme the app cannot actually render.
      if (btn.disabled) return;
      const theme = btn.dataset.theme;
      el.querySelectorAll('.theme-btn').forEach(b => {
        b.style.borderColor = 'var(--bg-tertiary)';
        b.style.background  = 'var(--bg-tertiary)';
      });
      btn.style.borderColor = 'var(--accent)';
      btn.style.background  = 'rgba(var(--accent-rgb),.08)';
      await api.settings.update({ theme }).catch(() => {});
      showToast(`Theme: ${theme}`);
    });
  });

  // Category interactions
  wireCategoryActions(el, cats);

  // Add category
  el.querySelector('#add-cat-btn').addEventListener('click', () => openCatSheet(el, null, cats));

  // Recurring expenses — reuses app.js's openExpenseSheet (already used by the
  // Expense quick-capture form), which now also refreshes Settings on save/delete.
  el.querySelector('#add-recurring-exp-btn')?.addEventListener('click', () => {
    window.openExpenseSheet?.({ isRecurring: true });
  });
  el.querySelectorAll('.edit-recurring-exp-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const exp = expenses.find(e => e.id === btn.dataset.expId);
      if (exp) window.openExpenseSheet?.(exp);
    });
  });
  el.querySelectorAll('.delete-recurring-exp-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      if (!confirm('Delete this recurring expense? This removes its whole history, not just future occurrences.')) return;
      try {
        await api.expenses.delete(btn.dataset.expId);
        const fresh = await api.expenses.list();
        el.querySelector('#recurring-exp-list').innerHTML = renderRecurringExpenses(fresh, cats);
      } catch (err) { showToast(err.message ?? 'Failed to delete', true); }
    });
  });

  // Expense category interactions (#63)
  el.querySelector('#add-expense-cat-btn')?.addEventListener('click', () => openExpenseCatSheet(el, null));
  wireExpenseCategoryActions(el, expenseCats);

  // Course schedule interactions
  el.querySelector('#add-class-btn')?.addEventListener('click', () => openClassSheet(el, null, classes));
  wireClassActions(el, classes);

  // Export
  el.querySelector('#export-btn')?.addEventListener('click', async () => {
    try {
      const [income, expenses, nw, tradesList, tasksList] = await Promise.all([
        api.income.list(), api.expenses.list(), api.networth.list(), api.trades.list(), api.tasks.list(),
      ]);
      const blob = new Blob([JSON.stringify({ income, expenses, networth: nw, trades: tradesList, tasks: tasksList }, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `personal-os-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    } catch { showToast('Export failed', true); }
  });

  // Import from backup
  const importInput = el.querySelector('#import-file-input');
  el.querySelector('#import-btn')?.addEventListener('click', () => importInput.click());
  importInput?.addEventListener('change', async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const result = await api.settings.import(data);
      const c = result.imported;
      const total = c.income + c.expenses + c.networth + c.trades + c.tasks;
      const errs  = result.errors ?? [];

      // The server already reports exactly which rows it rejected and why, in
      // `errors[]`. This used to ignore that array and toast the counts as a
      // success regardless — so a restore that imported *nothing* announced
      // "Imported: 0 income, 0 expenses, 0 snapshots, 0 trades, 0 tasks" in the
      // success style and then reloaded, which is the worst possible moment to
      // be reassuring (a user restores a backup precisely because they already
      // lost the data). Surface failure as failure; only reload when something
      // actually landed.
      if (total === 0) {
        showToast(
          errs.length
            ? `Import failed — nothing was restored. First problem: ${errs[0]}`
            : 'Import failed — nothing in that file could be restored.',
          true,
        );
        console.warn('[import] nothing restored. Row errors:', errs);
        return;
      }

      const summary = `Imported: ${c.income} income, ${c.expenses} expenses, ${c.networth} snapshots, ${c.trades} trades, ${c.tasks} tasks`;
      if (errs.length) {
        showToast(`${summary} — ${errs.length} row${errs.length === 1 ? '' : 's'} skipped`, true);
        console.warn('[import] skipped rows:', errs);
      } else {
        showToast(summary);
      }
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      showToast(err.message ?? 'Import failed — is this a Personal OS export file?', true);
    } finally {
      importInput.value = '';
    }
  });

  // Load sample data
  el.querySelector('#load-sample-btn')?.addEventListener('click', async () => {
    if (!confirm('Load sample data? This adds example income, expenses, and tasks you can explore, then clear later.')) return;
    try {
      await api.onboarding.loadSample();
      window.location.reload();
    } catch (err) {
      showToast(err.message ?? 'Failed to load sample data', true);
    }
  });

  // Clear sample data
  el.querySelector('#clear-sample-btn')?.addEventListener('click', async () => {
    if (!confirm('Remove all sample data?')) return;
    await api.onboarding.clearSample().catch(() => {});
    window.location.reload();
  });

  // Sign out
  el.querySelector('#signout-btn').addEventListener('click', async () => {
    await api.auth.signOut().catch(() => {});
    window.location.reload();
  });

  // Delete account — a plain confirm() is too easy to click through for
  // something this irreversible; require typing DELETE first.
  el.querySelector('#delete-account-btn')?.addEventListener('click', () => openDeleteAccountSheet());
}

function openDeleteAccountSheet() {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');

  content.innerHTML = `
    <div class="sheet-title" style="color:var(--negative)">Delete account</div>
    <p style="font-size:.875rem;color:var(--text-secondary);line-height:1.5;margin-bottom:var(--space-4)">
      This permanently deletes your account and everything in it — tasks, calendar blocks,
      school schedule, categories, settings, income, expenses, trades, and net worth snapshots.
      There is no undo and no backup.
    </p>
    <p style="font-size:.8rem;color:var(--text-tertiary);line-height:1.5;margin-bottom:var(--space-4)">
      "Export data as JSON" above only saves income, expenses, trades, net worth, and tasks —
      it does not include your calendar blocks, school schedule, categories, or settings, so
      exporting first won't preserve everything you're about to lose.
    </p>
    <div class="form-group">
      <label class="form-label">Type DELETE to confirm</label>
      <input type="text" id="delete-confirm-input" placeholder="DELETE" autocomplete="off">
    </div>
    <div class="form-group">
      <label class="form-label">Enter your password</label>
      <input type="password" id="delete-password-input" placeholder="Password" autocomplete="current-password">
    </div>
    <div id="delete-err" class="auth-error"></div>
    <div style="display:flex;gap:var(--space-3)">
      <button class="btn btn-ghost w-full" id="cancel-delete-btn" style="justify-content:center;padding:12px">Cancel</button>
      <button class="btn btn-danger w-full" id="confirm-delete-btn" style="justify-content:center;padding:12px" disabled>Delete forever</button>
    </div>`;

  const confirmInput = content.querySelector('#delete-confirm-input');
  const passwordInput = content.querySelector('#delete-password-input');
  const confirmBtn   = content.querySelector('#confirm-delete-btn');
  const updateBtnState = () => {
    confirmBtn.disabled = confirmInput.value.trim() !== 'DELETE' || passwordInput.value.length === 0;
  };
  confirmInput.addEventListener('input', updateBtnState);
  passwordInput.addEventListener('input', updateBtnState);

  content.querySelector('#cancel-delete-btn').addEventListener('click', closeSheet);

  confirmBtn.addEventListener('click', async () => {
    const errEl = content.querySelector('#delete-err');
    errEl.classList.remove('show');
    confirmBtn.disabled = true;
    confirmBtn.textContent = 'Deleting…';
    try {
      await api.settings.deleteAccount(passwordInput.value);
      window.location.reload();
    } catch (err) {
      errEl.textContent = err.message ?? 'Failed to delete account';
      errEl.classList.add('show');
      confirmBtn.disabled = false;
      confirmBtn.textContent = 'Delete forever';
    }
  });

  presentSheet();
  confirmInput.focus();
}

function wireCategoryActions(el, cats) {
  el.querySelectorAll('.edit-cat-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const catId = btn.dataset.catId;
      const cat = cats.find(c => c.id === catId);
      if (cat) openCatSheet(el, cat, cats);
    });
  });

  el.querySelectorAll('.delete-cat-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const catId = btn.dataset.catId;
      if (!confirm('Delete this category?')) return;
      try {
        await api.categories.delete(catId);
        const fresh = await api.categories();
        el.querySelector('#cat-list').innerHTML = renderCategoryList(fresh);
        wireCategoryActions(el, fresh);
      } catch (err) { showToast(err.message ?? 'Failed', true); }
    });
  });
}

function wireExpenseCategoryActions(el, cats) {
  const refresh = async () => {
    const fresh = await api.expenseCategories();
    el.querySelector('#expense-cat-list').innerHTML = renderExpenseCategoryList(fresh);
    wireExpenseCategoryActions(el, fresh);
  };

  el.querySelectorAll('.edit-expense-cat-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const cat = cats.find(c => c.id === btn.dataset.expenseCatId);
      if (cat) openExpenseCatSheet(el, cat);
    });
  });

  el.querySelectorAll('.delete-expense-cat-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      if (!confirm('Delete this expense category?')) return;
      try {
        await api.expenseCategories.delete(btn.dataset.expenseCatId);
        await refresh();
      } catch (err) {
        // The route refuses to delete a category still used by live expenses and
        // says how many — surface that rather than a generic failure, so the user
        // knows what to do about it.
        showToast(err.message ?? 'Failed', true);
      }
    });
  });
}

function openExpenseCatSheet(parentEl, existing) {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');
  const isEdit  = !!existing;
  const DEFAULT_COLOR = '#3B7EF0';
  const swatches = ['#3B7EF0','#06B6D4','#F97316','#A855F7','#12B981','#F43F8E','#F04B4B','#F0A030','#8B95B0'];
  const current  = existing?.color ?? DEFAULT_COLOR;

  content.innerHTML = `
    <div class="sheet-title">${isEdit ? 'Edit expense category' : 'New expense category'}</div>
    <div class="form-group">
      <label class="form-label">Name</label>
      <input type="text" id="expense-cat-name" value="${esc(existing?.name ?? '')}" placeholder="e.g. Childcare" required>
    </div>
    <div class="form-group">
      <label class="form-label">Color</label>
      <div style="display:flex;gap:var(--space-2);flex-wrap:wrap;margin-top:4px">
        ${swatches.map(c => `
          <div class="expense-color-swatch" data-color="${c}"
            style="width:28px;height:28px;border-radius:50%;background:${c};cursor:pointer;border:3px solid ${current === c ? '#fff' : 'transparent'};transition:border-color .15s">
          </div>`).join('')}
      </div>
      <input type="color" id="expense-cat-color" value="${current}" style="margin-top:var(--space-3);width:60px;height:32px;cursor:pointer">
    </div>
    <div id="expense-cat-err" class="auth-error"></div>
    <button class="btn btn-primary w-full" id="save-expense-cat-btn" style="justify-content:center;padding:12px">${isEdit ? 'Save changes' : 'Add category'}</button>`;

  content.querySelectorAll('.expense-color-swatch').forEach(sw => {
    sw.addEventListener('click', () => {
      content.querySelectorAll('.expense-color-swatch').forEach(s => { s.style.borderColor = 'transparent'; });
      sw.style.borderColor = '#fff';
      content.querySelector('#expense-cat-color').value = sw.dataset.color;
    });
  });
  content.querySelector('#expense-cat-color').addEventListener('input', () => {
    content.querySelectorAll('.expense-color-swatch').forEach(s => { s.style.borderColor = 'transparent'; });
  });

  content.querySelector('#save-expense-cat-btn').addEventListener('click', async () => {
    const errEl = content.querySelector('#expense-cat-err');
    errEl.classList.remove('show');
    const name  = content.querySelector('#expense-cat-name').value.trim();
    const color = content.querySelector('#expense-cat-color').value;
    if (!name) { errEl.textContent = 'Name is required'; errEl.classList.add('show'); return; }
    try {
      if (isEdit) await api.expenseCategories.update(existing.id, { name, color });
      else        await api.expenseCategories.create({ name, color });
      closeSheet();
      const fresh = await api.expenseCategories();
      parentEl.querySelector('#expense-cat-list').innerHTML = renderExpenseCategoryList(fresh);
      wireExpenseCategoryActions(parentEl, fresh);
    } catch (err) {
      errEl.textContent = err.message ?? 'Failed';
      errEl.classList.add('show');
    }
  });

  presentSheet();
}

// ── CATEGORY SHEET ─────────────────────────────────────────────────────────

function openCatSheet(parentEl, existing, cats) {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');

  const isEdit = !!existing;
  content.innerHTML = `
    <div class="sheet-title">${isEdit ? 'Edit category' : 'New category'}</div>
    <div class="form-group">
      <label class="form-label">Name</label>
      <input type="text" id="cat-name" value="${esc(existing?.name ?? '')}" placeholder="e.g. Side Projects" required>
    </div>
    <div class="form-group">
      <label class="form-label">Color</label>
      <div style="display:flex;gap:var(--space-2);flex-wrap:wrap;margin-top:4px">
        ${[...Object.values(CATEGORY_COLORS), '#F0A030','#EDF0F7','#F04B4B','#8B5CF6'].map(c => `
          <div class="color-swatch${(existing?.color ?? CATEGORY_COLORS.work) === c ? ' selected' : ''}" data-color="${c}"
            style="width:28px;height:28px;border-radius:50%;background:${c};cursor:pointer;border:3px solid ${(existing?.color ?? CATEGORY_COLORS.work) === c ? '#fff' : 'transparent'};transition:border-color .15s">
          </div>`).join('')}
      </div>
      <input type="color" id="cat-color" value="${existing?.color ?? CATEGORY_COLORS.work}" style="margin-top:var(--space-3);width:60px;height:32px;cursor:pointer">
    </div>
    <div id="cat-err" class="auth-error"></div>
    <div style="display:flex;gap:var(--space-3)">
      <button class="btn btn-primary w-full" id="save-cat-btn" style="justify-content:center;padding:12px">${isEdit ? 'Save changes' : 'Add category'}</button>
    </div>`;

  content.querySelectorAll('.color-swatch').forEach(sw => {
    sw.addEventListener('click', () => {
      content.querySelectorAll('.color-swatch').forEach(s => { s.style.borderColor = 'transparent'; });
      sw.style.borderColor = '#fff';
      content.querySelector('#cat-color').value = sw.dataset.color;
    });
  });
  content.querySelector('#cat-color').addEventListener('input', (e) => {
    content.querySelectorAll('.color-swatch').forEach(s => { s.style.borderColor = 'transparent'; });
  });

  content.querySelector('#save-cat-btn').addEventListener('click', async () => {
    const errEl  = content.querySelector('#cat-err');
    errEl.classList.remove('show');
    const name  = content.querySelector('#cat-name').value.trim();
    const color = content.querySelector('#cat-color').value;
    if (!name) { errEl.textContent = 'Name is required'; errEl.classList.add('show'); return; }
    try {
      if (isEdit) {
        await api.categories.update(existing.id, { name, color });
      } else {
        await api.categories.create({ name, color });
      }
      closeSheet();
      const fresh = await api.categories();
      parentEl.querySelector('#cat-list').innerHTML = renderCategoryList(fresh);
      wireCategoryActions(parentEl, fresh);
    } catch (err) {
      errEl.textContent = err.message ?? 'Failed';
      errEl.classList.add('show');
    }
  });

  presentSheet();
}

// ── HELPERS ─────────────────────────────────────────────────────────────────



function loadingSkeleton() {
  return `<div style="max-width:640px;margin:0 auto">
    <div class="skeleton" style="height:36px;width:160px;border-radius:8px;margin-bottom:24px"></div>
    ${[120, 200, 160, 140].map(h => `<div class="skeleton" style="height:${h}px;border-radius:16px;margin-bottom:16px"></div>`).join('')}
  </div>`;
}

// ── COURSE SCHEDULE ACTIONS ──────────────────────────────────────────────────

function wireClassActions(el, classes) {
  el.querySelectorAll('.edit-class-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const cls = classes.find(c => c.id === btn.dataset.classId);
      if (cls) openClassSheet(el, cls, classes);
    });
  });

  el.querySelectorAll('.delete-class-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const classId = btn.dataset.classId;
      if (!confirm('Remove this course from your schedule?')) return;
      try {
        await api.blocks.classes.delete(classId);
        const fresh = await api.blocks.classes.list();
        classes.length = 0; classes.push(...fresh);
        el.querySelector('#class-list').innerHTML = renderCourseSchedule(fresh);
        wireClassActions(el, fresh);
        showToast('Course removed');
      } catch (err) { showToast(err.message ?? 'Failed', true); }
    });
  });
}

function openClassSheet(parentEl, prefill = null, classes = []) {
  const overlay = document.getElementById('sheet-overlay');
  const sheet   = document.getElementById('sheet');
  const content = document.getElementById('sheet-content');
  const today   = new Date().toISOString().split('T')[0];
  const DAYS    = ['mon','tue','wed','thu','fri','sat','sun'];
  const DAY_LBL = { mon:'Mon', tue:'Tue', wed:'Wed', thu:'Thu', fri:'Fri', sat:'Sat', sun:'Sun' };

  const checkedDays = new Set((prefill?.dayOfWeek ?? '').split(',').map(d => d.trim()).filter(Boolean));

  content.innerHTML = `
    <div class="sheet-title">${prefill ? 'Edit course' : 'Add course'}</div>
    <div class="form-row">
      <div class="form-group">
        <label class="form-label">Course code *</label>
        <input type="text" id="cls-code" value="${esc(prefill?.courseCode ?? '')}" placeholder="CS101" style="text-transform:uppercase">
      </div>
      <div class="form-group">
        <label class="form-label">Course name *</label>
        <input type="text" id="cls-name" value="${esc(prefill?.courseName ?? '')}" placeholder="Intro to CS">
      </div>
    </div>
    <div class="form-group">
      <label class="form-label">Days of week *</label>
      <div style="display:flex;gap:var(--space-2);flex-wrap:wrap">
        ${DAYS.map(d => `
          <label style="display:flex;align-items:center;gap:4px;cursor:pointer;font-size:.8rem;
            background:${checkedDays.has(d) ? 'var(--accent)' : 'var(--bg-tertiary)'};
            color:${checkedDays.has(d) ? '#fff' : 'var(--text-secondary)'};
            border-radius:6px;padding:4px 10px;transition:all .15s" id="day-lbl-${d}">
            <input type="checkbox" id="day-${d}" ${checkedDays.has(d) ? 'checked' : ''} style="display:none">
            ${DAY_LBL[d]}
          </label>`).join('')}
      </div>
    </div>
    <div class="form-row">
      <div class="form-group">
        <label class="form-label">Start time *</label>
        <input type="time" id="cls-start" value="${prefill?.startTime ?? ''}">
      </div>
      <div class="form-group">
        <label class="form-label">End time *</label>
        <input type="time" id="cls-end" value="${prefill?.endTime ?? ''}">
      </div>
    </div>
    <div class="form-group">
      <label class="form-label">Location (optional)</label>
      <input type="text" id="cls-location" value="${esc(prefill?.location ?? '')}" placeholder="Room 204 / Zoom link">
    </div>
    <div class="form-group">
      <label style="display:flex;align-items:center;gap:var(--space-2);cursor:pointer;font-size:.875rem">
        <input type="checkbox" id="cls-online" ${prefill?.isOnline ? 'checked' : ''}> This is an online course
      </label>
    </div>
    <div class="form-row">
      <div class="form-group">
        <label class="form-label">Semester start *</label>
        <input type="date" id="cls-sem-start" value="${prefill?.semesterStart ?? today}">
      </div>
      <div class="form-group">
        <label class="form-label">Semester end *</label>
        <input type="date" id="cls-sem-end" value="${prefill?.semesterEnd ?? ''}">
      </div>
    </div>
    <div id="cls-err" class="auth-error"></div>
    <div style="display:flex;gap:var(--space-3)">
      <button class="btn btn-primary w-full" id="save-cls-btn" style="justify-content:center;padding:12px">${prefill ? 'Save changes' : 'Add course'}</button>
      ${prefill ? `<button class="btn btn-danger" id="delete-cls-btn" style="padding:12px 16px">Delete</button>` : ''}
    </div>`;

  // Day pill toggle visuals
  DAYS.forEach(d => {
    const lbl = content.querySelector(`#day-lbl-${d}`);
    const chk = content.querySelector(`#day-${d}`);
    lbl?.addEventListener('click', () => {
      chk.checked = !chk.checked;
      lbl.style.background = chk.checked ? 'var(--accent)' : 'var(--bg-tertiary)';
      lbl.style.color      = chk.checked ? '#fff' : 'var(--text-secondary)';
    });
  });

  content.querySelector('#save-cls-btn').addEventListener('click', async () => {
    const errEl        = content.querySelector('#cls-err');
    errEl.classList.remove('show');
    const courseCode   = content.querySelector('#cls-code').value.trim().toUpperCase();
    const courseName   = content.querySelector('#cls-name').value.trim();
    const startTime    = content.querySelector('#cls-start').value;
    const endTime      = content.querySelector('#cls-end').value;
    const location     = content.querySelector('#cls-location').value.trim() || undefined;
    const isOnline     = content.querySelector('#cls-online').checked;
    const semesterStart= content.querySelector('#cls-sem-start').value;
    const semesterEnd  = content.querySelector('#cls-sem-end').value;
    const dayOfWeek    = DAYS.filter(d => content.querySelector(`#day-${d}`)?.checked).join(',');

    if (!courseCode)   { errEl.textContent = 'Course code required';   errEl.classList.add('show'); return; }
    if (!courseName)   { errEl.textContent = 'Course name required';   errEl.classList.add('show'); return; }
    if (!dayOfWeek)    { errEl.textContent = 'Select at least one day'; errEl.classList.add('show'); return; }
    if (!startTime)    { errEl.textContent = 'Start time required';    errEl.classList.add('show'); return; }
    if (!endTime)      { errEl.textContent = 'End time required';      errEl.classList.add('show'); return; }
    if (!semesterStart){ errEl.textContent = 'Semester start required'; errEl.classList.add('show'); return; }
    if (!semesterEnd)  { errEl.textContent = 'Semester end required';   errEl.classList.add('show'); return; }

    const body = { courseCode, courseName, startTime, endTime, location, isOnline, dayOfWeek, semesterStart, semesterEnd };
    try {
      if (prefill) {
        await api.blocks.classes.update(prefill.id, body);
      } else {
        await api.blocks.classes.create(body);
      }
      closeSheet();
      const fresh = await api.blocks.classes.list();
      classes.length = 0; classes.push(...fresh);
      parentEl.querySelector('#class-list').innerHTML = renderCourseSchedule(fresh);
      wireClassActions(parentEl, fresh);
      showToast(prefill ? 'Course updated' : 'Course added');
    } catch (err) {
      errEl.textContent = err.message ?? 'Failed';
      errEl.classList.add('show');
    }
  });

  content.querySelector('#delete-cls-btn')?.addEventListener('click', async () => {
    if (!confirm('Remove this course?')) return;
    await api.blocks.classes.delete(prefill.id).catch(() => {});
    closeSheet();
    const fresh = await api.blocks.classes.list();
    classes.length = 0; classes.push(...fresh);
    parentEl.querySelector('#class-list').innerHTML = renderCourseSchedule(fresh);
    wireClassActions(parentEl, fresh);
    showToast('Course removed');
  });

  presentSheet();
}
